import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, Package, Battery, Users, X } from 'lucide-react';
import { searchApi } from '../lib/api';
import { debounce } from 'es-toolkit';

export default function GlobalSearch() {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Debounce query update
    const debouncedSetQuery = useCallback(
        debounce((val: string) => {
            setDebouncedQuery(val);
        }, 300),
        []
    );

    // Update debounced query when input changes
    useEffect(() => {
        debouncedSetQuery(query);
    }, [query, debouncedSetQuery]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const { data, isLoading } = useQuery({
        queryKey: ['search', debouncedQuery],
        queryFn: () => searchApi.search(debouncedQuery),
        enabled: debouncedQuery.length >= 2,
    });

    const hasResults = data && (data.packs.length > 0 || data.cells.length > 0 || data.customers.length > 0);

    const handleResultClick = (path: string) => {
        setIsOpen(false);
        setQuery('');
        navigate(path);
    };

    return (
        <div className="relative w-full min-w-0 max-w-full" ref={containerRef}>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
                    placeholder="Search items..."
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                />
                {query && (
                    <button
                        onClick={() => setQuery('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                )}
            </div>

            {/* Results Dropdown */}
            {isOpen && query.length >= 2 && (
                <div className="absolute mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 max-h-[80vh] overflow-y-auto">
                    {isLoading ? (
                        <div className="p-4 text-center text-gray-500 flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Searching...</span>
                        </div>
                    ) : !hasResults ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            No results found for "{query}"
                        </div>
                    ) : (
                        <div className="py-2">
                            {/* Packs */}
                            {data.packs.length > 0 && (
                                <div>
                                    <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                                        Packs
                                    </div>
                                    {data.packs.map((pack) => (
                                        <button
                                            key={pack.pack_id}
                                            onClick={() => handleResultClick(`/packs/${pack.pack_id}`)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <Package className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">
                                                    {pack.pack_id}
                                                </div>
                                                {pack.batch_id && (
                                                    <div className="text-xs text-gray-500 truncate">
                                                        Batch: {pack.batch_id}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Customers */}
                            {data.customers.length > 0 && (
                                <div>
                                    {data.packs.length > 0 && <div className="h-px bg-gray-100 my-1" />}
                                    <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                                        Customers
                                    </div>
                                    {data.customers.map((cust) => (
                                        <button
                                            key={cust.customer}
                                            onClick={() => handleResultClick(`/customers?search=${encodeURIComponent(cust.customer)}`)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                                                <Users className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">
                                                    {cust.customer}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Cells */}
                            {data.cells.length > 0 && (
                                <div>
                                    {(data.packs.length > 0 || data.customers.length > 0) && <div className="h-px bg-gray-100 my-1" />}
                                    <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                                        Cells
                                    </div>
                                    {data.cells.map((cell) => (
                                        <button
                                            key={cell.cell_id}
                                            onClick={() => handleResultClick(`/cells/${cell.cell_id}`)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                                <Battery className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">
                                                    {cell.cell_id}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
